import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { GridPlugin } from '@df/catalog-ui/listing/plugin/grid/grid-plugin';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { ProductListingComponentDef } from '@df/catalog-ui/product/listing/product-listing.component.def';
import { Product as CoreProduct, IProductDataV2 } from '@df/catalog/product/product';
import { GtmCategoryToListPipe } from '@df/module-gtm/integration/gtm-category-to-list.pipe';
import { GtmItemDirective } from '@df/module-gtm/integration/gtm-item.directive';
import { GtmListDirective } from '@df/module-gtm/integration/gtm-list.directive';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { Subscription } from 'rxjs';
import { Product } from '../product/product.decorator';
import { ProductComponent } from '../product/product/product.component';
import { CATEGORY_LISTING_STEP } from './category-listing-step';
import { LoadMoreComponent } from './load-more/load-more.component';
import { ProductListingToolsComponent } from './product-listing-tools.component';
@Component({
    imports: [
        DfUiModule,
        ProductListingToolsComponent,
        GridModule,
        GtmListDirective,
        GtmItemDirective,
        ICmsClientModule,
        LoadMoreComponent,
        ProductComponent,
        GtmCategoryToListPipe,
        TranslatePipe
    ],
    selector: 'product-listing',
    templateUrl: 'product-listing.html',
    styleUrl: 'product-listing.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListingComponent extends ProductListingComponentDef<Product<IProductDataV2>> implements OnInit, OnDestroy {
    private initSubscription?: Subscription;

    private afterContentInitDirectiveService = inject(AfterContentInitDirectiveService);

    override ngOnInit() {
        this.initSubscription = this.afterContentInitDirectiveService.loaded.subscribe(value => {
            if (value) {
                this.initSubscription?.unsubscribe();
                this.initSubscription = undefined;
                this.detectChanges();
            }
        });
        super.ngOnInit();
    }

    override ngOnDestroy(): void {
        this.initSubscription?.unsubscribe();
        super.ngOnDestroy();
    }

    get asCoreListing(): ProductListing<CoreProduct<IProductDataV2>> | undefined {
        return this.listing as ProductListing<CoreProduct<IProductDataV2>> | undefined;
    }

    /**
     * How many tiles the server render shows, when it has to differ from the listing's own limit.
     *
     * The listing shows up to 100 products on the server but starts from CATEGORY_LISTING_STEP
     * in the browser, so once the app took over it cut the grid short: a visitor already
     * scrolling saw the tiles under them vanish and the content below jump into view. The
     * server now lays the grid out as the browser will, with the extra tiles still in the HTML
     * for crawlers.
     */
    protected get serverLimit(): number | undefined {
        if (this.app.isPlatformBrowser || this.context !== 'category' || !this.listing) {
            return undefined;
        }
        return this.listing.models.length > CATEGORY_LISTING_STEP ? CATEGORY_LISTING_STEP : undefined;
    }

    /**
     * Tiles shown at full height
     */
    protected get visibleTilesQty(): number {
        return this.serverLimit ?? this.listing?.models.length ?? 0;
    }

    /**
     * Tiles laid out at zero height after the visible ones, as the browser does with the next step
     */
    protected get overflowTilesQty(): number {
        if (!this.listing) {
            return 0;
        }
        const limit = this.serverLimit;
        return limit === undefined
            ? this.listing.modelsWithOverflow.length - this.listing.models.length
            : Math.min(this.listing.modelsWithOverflow.length, limit + CATEGORY_LISTING_STEP) - limit;
    }

    /**
     * Whether load-more shows its "all loaded" state, which is taller
     */
    protected get isLoadMoreComplete(): boolean {
        return this.serverLimit === undefined && !this.listing?.hasMore;
    }

    protected get overflowRowsQty(): number {
        if (!this.listing) {
            return 0;
        }
        if (this.serverLimit === undefined) {
            return this.listing.plugin.grid.getOverflowRowsQty();
        }
        const gridSize = this.listing.plugin.grid.gridSize();
        return gridSize ? Math.ceil(this.overflowTilesQty / gridSize) : 0;
    }

    /**
     * Install GridPlugin when listing is set
     */
    protected override onListingChange() {
        if (this.asCoreListing) {
            new GridPlugin(this.asCoreListing, {
                layouts: {
                    '1x1': ['S', 'M', 'L', 'X'],
                    '2x1': ['S', 'M', 'L', 'X']
                },
                gutter: {
                    col: ['16px', '16px', '20px', '20px'],
                    row: ['16px', '16px', '20px', '20px']
                }
            });
        }
        super.onListingChange();
    }
}
